import { ChevronRight } from "@mui/icons-material";
import { styled } from "@mui/material";
import { getStartIdx } from "components/Breadcrumbs/getStartIdx";
import { useWindowSize } from "helpers/useWindowSize";
import { useEffect, useRef, useState } from "react";
import { Route } from "routes";
import { BreadcrumbItem } from "./BreadcrumbItem";

const PREFIX = "Breadcrumbs";

const classes = {
	root: `${PREFIX}-root`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		display: "flex",
		alignItems: "center",
		paddingLeft: theme.spacing(1),
	},
}));

export interface BreadcrumbsProps {
	items: Route[];
}

export const Breadcrumbs = ({ items }: BreadcrumbsProps): JSX.Element => {
	const breadcrumbs: JSX.Element[] = [];

	// Handle different screen widths
	const widths = useRef<number[]>([]);
	const ref = useRef<HTMLDivElement | null>(null);
	const [startIdx, setStartIdx] = useState(0);

	const windowSize = useWindowSize();

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		if (widths.current.length === 0) {
			// We use `Array.from`, because `getClientRects` doesn't return an array,
			// instead it returns an iterable. This is similar to `FileList`.
			widths.current = Array.from(ref.current.getClientRects()).map(
				(i) => i.width,
			);
		}

		const boundingRect = ref.current.getBoundingClientRect();

		const maxWidth = window.innerWidth - boundingRect.x;
		if (maxWidth === boundingRect.right) {
			return;
		}

		const idx = getStartIdx(boundingRect.x + 100, widths.current, maxWidth);

		setStartIdx(idx);
	}, [windowSize.innerWidth]);

	// Get the breadcrumbs
	for (let i = startIdx; i < items.length; i += 1) {
		const route = items[i]!;
		breadcrumbs.push(
			<BreadcrumbItem key={route.path} route={route} />,
			<ChevronRight key={i} aria-hidden data-testid="chevron" />,
		);
	}

	breadcrumbs.pop();

	return (
		<Root className={classes.root} ref={ref}>
			{breadcrumbs}
		</Root>
	);
};
